<script lang="ts">
  import { onMount } from 'svelte'
  import { intake } from '$lib/public/intake-client'
  import { showError } from '$lib/util/errors'
  import FormField from '../../components/FormField.svelte'
  import BreedCombobox from '../../components/BreedCombobox.svelte'
  import type { Draft } from '$lib/public/wizard-state'

  interface ApiError {
    detail?: string
  }

  interface Props {
    animal: Draft['animal']
  }
  let { animal = $bindable() }: Props = $props()

  let breedOptions = $state<{ id: number; name: string; species: 'DOG' | 'CAT' | 'OTHER' }[]>([])

  onMount(async () => {
    const { data, error } = await intake.GET('/public/breeds', {})
    if (error) {
      showError(error as ApiError)
      return
    }
    breedOptions = data?.items ?? []
  })

  // Breeds matching the selected pet type (Other shows none).
  const filteredBreeds = $derived(
    animal.species === 'DOG' || animal.species === 'CAT'
      ? breedOptions.filter((b) => b.species === animal.species)
      : [],
  )

  function selectSpecies(s: 'DOG' | 'CAT' | 'OTHER'): void {
    if (animal.species === s) return
    animal.species = s
    // A breed from the old type no longer applies.
    animal.breedId = null
  }
</script>

<h2 tabindex="-1">Tell us about your animal</h2>
<p class="muted">Step one of three.</p>

<FormField label="Animal name" name="animal-name" bind:value={animal.name} required />

<div class="form-field">
  <span class="label">Pet type</span>
  <div class="type-row" role="group" aria-label="Pet type">
    {#each [['DOG', 'Dog'], ['CAT', 'Cat'], ['OTHER', 'Other']] as [value, text] (value)}
      <button
        type="button"
        class="type-btn"
        class:selected={animal.species === value}
        aria-pressed={animal.species === value}
        onclick={() => selectSpecies(value as 'DOG' | 'CAT' | 'OTHER')}
      >
        {text}
      </button>
    {/each}
  </div>
</div>

{#if animal.species === 'DOG' || animal.species === 'CAT'}
  <BreedCombobox breeds={filteredBreeds} bind:breedId={animal.breedId} />
{:else if animal.species === 'OTHER'}
  <FormField
    label="What kind of animal? (optional)"
    name="animal-species-other"
    bind:value={animal.speciesOther}
  />
{/if}

<FormField label="Birthdate" name="animal-birthdate" type="date" bind:value={animal.birthdate} />
<FormField label="Color" name="animal-color" bind:value={animal.color} />

<style>
  h2 {
    margin: 0 0 4px;
    font-size: 1.4rem;
    color: var(--ink-strong);
  }
  .muted {
    margin: 0 0 20px;
    color: var(--muted);
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .type-row {
    display: flex;
    gap: 8px;
  }
  .type-btn {
    flex: 1;
    min-height: 44px; /* comfortable touch target */
    padding: 10px 12px;
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    background: var(--surface-2);
    color: var(--ink);
    font-size: 1rem;
    cursor: pointer;
  }
  .type-btn.selected {
    border-color: var(--brand);
    background: var(--brand-tint);
    /* --link (text-tuned) not --brand: brand is only 3.8:1 on brand-tint. */
    color: var(--link);
    font-weight: 500;
  }
  h2:focus {
    outline: none;
  }
</style>
